import { useState } from 'react'

import { Box, FontsWeights, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'
import { PerformanceProcessAgreementCourseFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import CourseRequestWizard from 'modules/performance_process/components/CourseRequestWizard'
import trainingsUpsell from 'modules/performance_process/lib/assets/trainingsUpsell.png'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { TrainingRequestItem } from './TrainingRequestItem'

type WizardMode = 'fromCatalog' | 'newProposal' | null

export const AgreementCourse = ({
  agreement,
  HeaderComponent = Text.Normal,
}: {
  agreement: PerformanceProcessAgreementCourseFragment
  HeaderComponent?: React.ComponentType<{ children: React.ReactNode; weight: FontsWeights }>
}) => {
  const [wizardMode, setWizardMode] = useState<WizardMode>(null)

  const trainingRequests = agreement.agreementTrainingRequests ?? []
  const canAddTrainingRequest = agreement._policies?.addTrainingRequest?.can

  if (!canAddTrainingRequest && trainingRequests.length === 0) return null

  const handleOpenWizard = (mode: WizardMode) => {
    setWizardMode(mode)
  }

  const handleCloseWizard = () => {
    setWizardMode(null)
  }

  const dropdownItems = [
    {
      label: i18n.t('trainings.training_requests.create.button.from_catalog'),
      onClick: () => handleOpenWizard('fromCatalog'),
    },
    {
      label: i18n.t('trainings.training_requests.create.button.new'),
      onClick: () => handleOpenWizard('newProposal'),
    },
  ]

  return (
    <Box gap="s24">
      <HeaderComponent weight="semibold">
        {i18n.t('performance_process.targets.suggested_courses')}
      </HeaderComponent>
      {trainingRequests.map(({ trainingRequest }, index) => (
        <TrainingRequestItem
          trainingRequest={trainingRequest}
          index={index + 1}
          key={trainingRequest.id}
        />
      ))}
      {wizardMode && (
        <CourseRequestWizard
          onClose={handleCloseWizard}
          agreement={agreement}
          fromCatalog={wizardMode === 'fromCatalog'}
        />
      )}
      {!agreement.locked && (
        <Box width="fit-content">
          <UpsellingKit.ProductWidgetPopover
            label={i18n.t('performance_process.targets.request_new_course')}
            mediaUrl={trainingsUpsell}
            variant="outlinePromote"
            title={i18n.t('upsell_trainings.popover.title')}
            description={i18n.t('upsell_trainings.popover.description')}
            requestSource="Performance - suggested courses"
            moduleRequested={AvailableModules.trainings}
            requestReason={ValidReasons.MissingAddon}
            fallbackComponent={
              canAddTrainingRequest && (
                <Box gap="s12" className="flex justify-start">
                  <Dropdown items={dropdownItems}>
                    <F0Button
                      variant="outline"
                      icon={Add}
                      label={i18n.t('performance_process.targets.request_new_course')}
                    />
                  </Dropdown>
                </Box>
              )
            }
          />
        </Box>
      )}
    </Box>
  )
}
